import React from 'react';

import { useUser } from '@repo/hooks/useUser';
import { POLICIES } from '@/lib/Authorization';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { TypeOf, z } from 'zod';
import { Form } from '@repo/ui/Form';
import { TextInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';
import { Card } from '@repo/ui/Card';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { useAccount } from '@/hooks/useAccount';
import { ACCOUNT_CONTACTS, AccountContactKey } from '@repo/types/accountContacts';
import { Mercury } from '@repo/types/Models';
import { getGraphQLErrorMessage } from '@repo/util/getGraphQLErrorMessage';

type AccountPrimaryContact = Mercury.Models.AccountPrimaryContact;

const accountContactFormSchema = z.object({
  firstName: z.string().min(1, { message: 'Please include a first name.' }),
  lastName: z.string().min(1, { message: 'Please include a last name.' }),
  email: z.string().email({ message: 'Please submit a valid email address.' }),
  phoneNumber: z.string().min(7, { message: 'Please submit a valid phone number.' }),
  title: z.string().optional(),
  mobilePhoneNumber: z
    .string()
    .min(7, { message: 'Please submit a valid mobile phone number.' })
    .optional()
    .or(z.literal('')),
});

type AccountContactFormParams = TypeOf<typeof accountContactFormSchema>;

interface AccountContactFormProps {
  contactKey: AccountContactKey;
  contact: AccountPrimaryContact | null;
  refetch: () => void;
}

const AccountContactForm = ({ contactKey, contact, refetch }: AccountContactFormProps) => {
  const user = useUser();
  const currentAccount = useAccount();
  const client = useApolloClient();
  const contactConfig = ACCOUNT_CONTACTS[contactKey];

  const userCanCreate = POLICIES['account_primary_contacts:create'](user);
  const userCanUpdate = POLICIES['account_primary_contacts:update'](user);
  const isEditable = contact ? userCanUpdate : userCanCreate;

  const CREATE_MUTATION = gql`
    mutation CreatePrimaryContactForAccount(
      $accountId: ID!
      $input: CreatePrimaryContactForAccountInput!
    ) {
      createPrimaryContactForAccount(accountId: $accountId, input: $input) {
        accountPrimaryContact {
          id
        }
      }
    }
  `;

  const UPDATE_MUTATION = gql`
    mutation UpdateAccountPrimaryContact($id: ID!, $input: UpdateAccountPrimaryContactInput!) {
      updateAccountPrimaryContact(id: $id, input: $input) {
        accountPrimaryContact {
          id
        }
      }
    }
  `;

  const onSubmit = async (input: AccountContactFormParams) => {
    try {
      if (contact) {
        await client.mutate({
          mutation: UPDATE_MUTATION,
          variables: {
            id: contact.id,
            input: { ...input, label: contactKey },
          },
        });
      } else {
        await client.mutate({
          mutation: CREATE_MUTATION,
          variables: {
            accountId: currentAccount?.id,
            input: { ...input, label: contactKey },
          },
        });
      }
    } catch (error) {
      const action = contact ? 'update' : 'create';
      throw new Error(
        getGraphQLErrorMessage(error) ??
          `Error while trying to ${action} contact. Please try again later or contact support if error persists.`
      );
    }
    refetch();
  };

  const form = useZodForm({
    schema: accountContactFormSchema,
    onSubmit,
    defaultValues: contact
      ? {
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phoneNumber: contact.phoneNumber,
          title: contact.title ?? '',
          mobilePhoneNumber: contact.mobilePhoneNumber ?? '',
        }
      : undefined,
  });

  return (
    <Card title={contactConfig.label}>
      <Form {...form} className="grid gap-6 grid-col-1 xs:grid-cols-2">
        <Form.Success>Contact successfully {contact ? 'updated' : 'created'}.</Form.Success>
        <Form.Error autoClose={15000} />
        <TextInputField label="Title" disabled={!isEditable} {...registerInput(form, 'title')} />
        <TextInputField
          label="First Name"
          disabled={!isEditable}
          {...registerInput(form, 'firstName')}
        />
        <TextInputField
          label="Last Name"
          disabled={!isEditable}
          {...registerInput(form, 'lastName')}
        />
        <TextInputField
          label="Email address"
          disabled={!isEditable}
          {...registerInput(form, 'email')}
        />
        <TextInputField
          label="Phone number"
          disabled={!isEditable}
          {...registerInput(form, 'phoneNumber')}
        />
        <TextInputField
          label="Mobile phone number"
          disabled={!isEditable}
          {...registerInput(form, 'mobilePhoneNumber')}
        />
        <div className="w-full">
          <Button type="submit" disabled={!isEditable}>
            {contact ? 'Update' : 'Create'} Contact
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default AccountContactForm;
